import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import WorkFormPage from '../../../components/work/WorkFormPage';
import WorkManageEntityModal from '../../../components/work/WorkManageEntityModal';
import WorkSelectWithManage from '../../../components/work/WorkSelectWithManage';
import { tasksApi, taskCategoriesApi, taskLabelsApi, taskboardsColumnsApi, taskFilesApi } from '../../../utils/workApi';
import { show422Error } from '../../../utils/api';
import { scrollToFirstError } from '../../../utils/formHelpers';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { FormSkeleton } from '../../../components/SkeletonLoaders';
import {
  Button,
  Card,
  Form,
  Input,
  Select,
  Row,
  Col,
  message,
  InputNumber,
  Modal,
  Upload,
  Tag,
} from 'antd';
import { CheckSquareOutlined, InboxOutlined, SaveOutlined } from '@ant-design/icons';
import PersianDatePicker from '../../../components/PersianDatePicker';
import { PANEL_PRIMARY_BTN_CLASS } from '../../../constants/ui';
import {
  buildTaskLabelPayload,
  buildTaskPayload,
  buildTaskboardColumnPayload,
} from '../../../utils/workPayloadBuilders';
import WorkCheckbox from '../../../components/work/WorkCheckbox';
import WorkColorPickerField from '../../../components/work/WorkColorPickerField';
import { getTodayJalali } from '../../../utils/dateUtils';
import { useEntityBreadcrumb } from '../../../hooks/useEntityBreadcrumb';

const { TextArea } = Input;
const { Option } = Select;
const label = (t) => <span style={{ fontFamily: 'CustomFont' }}>{t}</span>;

const sortColumns = (cols = []) =>
  [...cols].sort((a, b) => {
    const pa = Number(a.priority ?? a.column_order ?? a.id ?? 0);
    const pb = Number(b.priority ?? b.column_order ?? b.id ?? 0);
    return pa - pb;
  });

const resolveCreatedTaskId = (res) =>
  res?.task?.id || res?.data?.task?.id || res?.data?.id || res?.id || null;

const AddTask = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const copyFromId = location.state?.copyFromId;
  const returnTo = location.state?.returnTo;
  const { canAdd } = useResourcePermissions('tasks');
  const { trail, goToCrumb } = useEntityBreadcrumb({
    listPath: '/work/tasks',
    listLabel: 'مدیریت وظایف',
    entityLabel: copyFromId ? 'کپی وظیفه' : 'وظیفه جدید',
  });
  const [form] = Form.useForm();
  const [colForm] = Form.useForm();
  const [fetching, setFetching] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [labels, setLabels] = useState([]);
  const [columns, setColumns] = useState([]);
  const [options, setOptions] = useState({ projects: [], employees: [], milestones: [] });
  const [catModal, setCatModal] = useState(false);
  const [labelModal, setLabelModal] = useState(false);
  const [colModal, setColModal] = useState(false);
  const [catModalOpening, setCatModalOpening] = useState(false);
  const [labelModalOpening, setLabelModalOpening] = useState(false);
  const [colModalOpening, setColModalOpening] = useState(false);
  const [colModalSaving, setColModalSaving] = useState(false);
  const [fileList, setFileList] = useState([]);
  const withoutDuedate = Form.useWatch('without_duedate', form);

  const refreshMeta = useCallback(async () => {
    const res = await tasksApi.getCreateData();
    const cols = sortColumns(res?.taskboard_columns || []);
    setCategories(res?.categories || []);
    setLabels(res?.task_labels || []);
    setColumns(cols);
    setOptions({
      projects: res?.projects || [],
      employees: res?.employees || [],
      milestones: res?.milestones || [],
    });
    return { ...res, taskboard_columns: cols };
  }, []);

  const loadCategories = useCallback(async () => {
    const res = await taskCategoriesApi.getCreateData();
    const list = res?.categories || [];
    setCategories(list);
    return list;
  }, []);

  const loadLabels = useCallback(async () => {
    const res = await taskLabelsApi.getCreateData();
    const list = res?.labels || [];
    setLabels(list);
    return list;
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const meta = await refreshMeta();
        const cols = meta?.taskboard_columns || [];
        const colFromQuery = searchParams.get('board_column_id');
        const defaultCol =
          (colFromQuery ? Number(colFromQuery) || colFromQuery : null) || cols[0]?.id;
        const today = getTodayJalali('jYYYY/jMM/jDD');

        form.setFieldsValue({
          priority: 'medium',
          repeat_type: 'day',
          start_date: today,
          board_column_id: defaultCol,
        });

        if (copyFromId) {
          const editRes = await tasksApi.getEditData(copyFromId);
          const t = editRes?.task || editRes?.data || editRes;
          if (t) {
            form.setFieldsValue({
              heading: t.heading ? `${t.heading} (کپی)` : undefined,
              description: t.description,
              project_id: t.project_id || t.project?.id,
              category_id: t.task_category_id || t.category_id || t.category?.id,
              task_labels: (t.labels || t.task_labels || []).map((l) => l.id || l),
              priority: t.priority || 'medium',
              board_column_id: t.board_column_id || t.board_column?.id || defaultCol,
              start_date: t.start_date || today,
              due_date: t.due_date || t.due_on,
              without_duedate: !(t.due_date || t.due_on),
              user_id: (t.users || []).map((u) => u.id || u.user_id).filter(Boolean),
              estimate_hours: t.estimate_hours || t.estimate,
              billable: t.billable === 'yes' || t.billable === 1 || t.billable === true,
              milestone_id: t.milestone_id || t.milestone?.id,
            });
          }
        }
      } finally {
        setFetching(false);
      }
    };
    init();
  }, [refreshMeta, searchParams, form, copyFromId]);

  useEffect(() => {
    if (!fetching && !canAdd) navigate('/work/tasks', { replace: true });
  }, [fetching, canAdd, navigate]);

  const openCatModal = async () => {
    setCatModalOpening(true);
    try {
      await loadCategories();
      setCatModal(true);
    } finally {
      setCatModalOpening(false);
    }
  };

  const openLabelModal = async () => {
    setLabelModalOpening(true);
    try {
      await loadLabels();
      setLabelModal(true);
    } finally {
      setLabelModalOpening(false);
    }
  };

  const openColModal = () => {
    colForm.resetFields();
    setColModal(true);
  };

  const onFinish = async (values) => {
    setSubmitting(true);
    try {
      const res = await tasksApi.create(buildTaskPayload(values));
      const taskId = resolveCreatedTaskId(res);
      const files = fileList.map((f) => f.originFileObj).filter(Boolean);
      if (taskId && files.length) {
        try {
          await taskFilesApi.upload(taskId, files);
        } catch {
          message.warning('وظیفه ایجاد شد ولی آپلود فایل ناموفق بود');
          navigate(returnTo || `/work/tasks/${taskId}`);
          return;
        }
      }
      message.success('وظیفه ایجاد شد');
      navigate(returnTo || (taskId ? `/work/tasks/${taskId}` : '/work/tasks'));
    } catch (e) {
      show422Error(e, form);
    } finally {
      setSubmitting(false);
    }
  };

  const saveColumn = async () => {
    const v = await colForm.validateFields();
    setColModalSaving(true);
    try {
      await taskboardsColumnsApi.create(buildTaskboardColumnPayload(v));
      message.success('ستون اضافه شد');
      setColModal(false);
      colForm.resetFields();
      const meta = await refreshMeta();
      if (!form.getFieldValue('board_column_id') && meta?.taskboard_columns?.[0]?.id) {
        form.setFieldValue('board_column_id', meta.taskboard_columns[0].id);
      }
    } catch {
      message.error('خطا در ذخیره');
    } finally {
      setColModalSaving(false);
    }
  };

  const labelOptionLabel = (l) => (
    <Tag
      color={l.label_color || l.color || 'default'}
      className="m-0"
      style={{ fontFamily: 'CustomFont' }}
    >
      {l.label_name || l.name}
    </Tag>
  );

  return (
    <WorkFormPage
      title="وظیفه جدید"
      subtitle="اطلاعات وظیفه را تکمیل کنید"
      icon={CheckSquareOutlined}
      iconClassName="bg-violet-50 dark:bg-violet-900/20"
      iconColorClass="text-violet-600 dark:text-violet-400"
      backPath="/work/tasks"
      breadcrumbTrail={trail}
      onBreadcrumbNavigate={goToCrumb}
    >
      {fetching ? (
        <FormSkeleton rows={10} columns={2} />
      ) : (
        <Card className="shadow-lg border-0 bg-white dark:bg-gray-800">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            onFinishFailed={() => scrollToFirstError(form)}
            scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
            initialValues={{ priority: 'medium', repeat_type: 'day' }}
            className="work-panel-form"
          >
            <Row gutter={[16, 0]}>
              <Col xs={24}>
                <Form.Item
                  name="heading"
                  label={label('عنوان')}
                  rules={[{ required: true, message: 'عنوان الزامی است' }]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <WorkSelectWithManage
                  label="وضعیت"
                  name="board_column_id"
                  required
                  rules={[{ required: true, message: 'وضعیت الزامی است' }]}
                  onManageClick={openColModal}
                  manageLoading={colModalOpening}
                  manageTooltip="مدیریت وضعیت‌ها"
                >
                  {columns.map((c) => (
                    <Option key={c.id} value={c.id} label={c.column_name}>
                      {c.column_name}
                    </Option>
                  ))}
                </WorkSelectWithManage>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item name="priority" label={label('اولویت')}>
                  <Select>
                    <Option value="high">بالا</Option>
                    <Option value="medium">متوسط</Option>
                    <Option value="low">پایین</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name="start_date"
                  label={label('تاریخ شروع')}
                  rules={[{ required: true, message: 'تاریخ شروع الزامی است' }]}
                >
                  <PersianDatePicker className="w-full" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name="due_date"
                  label={label('سررسید')}
                  rules={
                    withoutDuedate
                      ? []
                      : [{ required: true, message: 'سررسید الزامی است' }]
                  }
                >
                  <PersianDatePicker className="w-full" disabled={withoutDuedate} />
                </Form.Item>
                <Form.Item name="without_duedate" valuePropName="checked" className="mb-0 -mt-2">
                  <WorkCheckbox>بدون سررسید</WorkCheckbox>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item name="project_id" label={label('پروژه')}>
                  <Select allowClear showSearch optionFilterProp="label">
                    {options.projects.map((p) => (
                      <Option key={p.id} value={p.id} label={p.project_name}>
                        {p.project_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <WorkSelectWithManage
                  label="دسته"
                  name="category_id"
                  onManageClick={openCatModal}
                  manageLoading={catModalOpening}
                >
                  {categories.map((c) => (
                    <Option key={c.id} value={c.id} label={c.category_name}>
                      {c.category_name}
                    </Option>
                  ))}
                </WorkSelectWithManage>
              </Col>

              <Col xs={24}>
                <WorkSelectWithManage
                  label="تگ‌ها"
                  name="task_labels"
                  onManageClick={openLabelModal}
                  manageLoading={labelModalOpening}
                  selectProps={{
                    mode: 'multiple',
                    allowClear: true,
                    optionLabelProp: 'label',
                    tagRender: (props) => {
                      const { value, closable, onClose } = props;
                      const l = labels.find((x) => String(x.id) === String(value));
                      return (
                        <Tag
                          color={l?.label_color || l?.color || 'default'}
                          closable={closable}
                          onClose={onClose}
                          style={{ marginInlineEnd: 4, fontFamily: 'CustomFont' }}
                        >
                          {l?.label_name || l?.name || value}
                        </Tag>
                      );
                    },
                  }}
                >
                  {labels.map((l) => (
                    <Option
                      key={l.id}
                      value={l.id}
                      label={l.label_name || l.name}
                    >
                      {labelOptionLabel(l)}
                    </Option>
                  ))}
                </WorkSelectWithManage>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item name="user_id" label={label('مسئولین')}>
                  <Select mode="multiple" allowClear optionFilterProp="label">
                    {options.employees.map((e) => (
                      <Option key={e.id} value={e.id} label={e.name}>
                        {e.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item name="milestone_id" label={label('مرحله')}>
                  <Select allowClear>
                    {options.milestones.map((m) => (
                      <Option key={m.id} value={m.id}>
                        {m.milestone_title || m.title}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16} className="mt-2">
              <Col xs={24} sm={8}>
                <Form.Item name="is_private" valuePropName="checked">
                  <WorkCheckbox>خصوصی</WorkCheckbox>
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="billable" valuePropName="checked">
                  <WorkCheckbox>قابل‌صورتحساب</WorkCheckbox>
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="set_time_estimate" valuePropName="checked">
                  <WorkCheckbox>برآورد زمان</WorkCheckbox>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item noStyle shouldUpdate={(a, b) => a.set_time_estimate !== b.set_time_estimate}>
              {({ getFieldValue }) =>
                getFieldValue('set_time_estimate') ? (
                  <Row gutter={16}>
                    <Col xs={12} md={6}>
                      <Form.Item name="estimate_hours" label={label('ساعت')}>
                        <InputNumber className="w-full" min={0} />
                      </Form.Item>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Item name="estimate_minutes" label={label('دقیقه')}>
                        <InputNumber className="w-full" min={0} max={59} />
                      </Form.Item>
                    </Col>
                  </Row>
                ) : null
              }
            </Form.Item>

            <Form.Item name="repeat" valuePropName="checked">
              <WorkCheckbox>تکرار</WorkCheckbox>
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(a, b) => a.repeat !== b.repeat}>
              {({ getFieldValue }) =>
                getFieldValue('repeat') ? (
                  <Row gutter={16}>
                    <Col xs={8}>
                      <Form.Item name="repeat_count" label={label('تعداد')}>
                        <InputNumber className="w-full" min={1} />
                      </Form.Item>
                    </Col>
                    <Col xs={8}>
                      <Form.Item name="repeat_type" label={label('نوع تکرار')}>
                        <Select>
                          <Option value="day">روز</Option>
                          <Option value="week">هفته</Option>
                          <Option value="month">ماه</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={8}>
                      <Form.Item name="repeat_cycles" label={label('چرخه')}>
                        <InputNumber className="w-full" min={1} />
                      </Form.Item>
                    </Col>
                  </Row>
                ) : null
              }
            </Form.Item>

            <Form.Item label={label('فایل‌ها')}>
              <Upload.Dragger
                multiple
                fileList={fileList}
                beforeUpload={() => false}
                onChange={({ fileList: next }) => setFileList(next)}
                style={{ fontFamily: 'CustomFont' }}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text" style={{ fontFamily: 'CustomFont' }}>
                  فایل را بکشید یا کلیک کنید
                </p>
              </Upload.Dragger>
            </Form.Item>

            <Form.Item name="description" label={label('توضیحات')}>
              <TextArea rows={4} />
            </Form.Item>

            <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4 border-t border-gray-100 dark:border-gray-700 mt-4">
              <Button onClick={() => navigate('/work/tasks')}>انصراف</Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={submitting}
                className={PANEL_PRIMARY_BTN_CLASS}
              >
                ذخیره
              </Button>
            </div>
          </Form>
        </Card>
      )}

      <WorkManageEntityModal
        open={catModal}
        onClose={() => setCatModal(false)}
        title="دسته وظیفه"
        nameField="category_name"
        nameLabel="نام دسته"
        loadItems={loadCategories}
        onCreate={(name) => taskCategoriesApi.create(name)}
        onUpdate={(cid, name) => taskCategoriesApi.update(cid, name)}
        onDelete={(cid) => taskCategoriesApi.delete(cid)}
        buildCreatePayload={(v) => v.category_name}
        buildUpdatePayload={(cid, name) => name}
        getItemLabel={(i) => i.category_name}
      />
      <WorkManageEntityModal
        open={labelModal}
        onClose={() => {
          setLabelModal(false);
          loadLabels();
        }}
        title="تگ وظیفه"
        nameField="label_name"
        nameLabel="نام تگ"
        loadItems={loadLabels}
        onCreate={(body) => taskLabelsApi.create(buildTaskLabelPayload(body))}
        onUpdate={(lid, body) => taskLabelsApi.update(lid, body)}
        onDelete={(lid) => taskLabelsApi.delete(lid)}
        buildCreatePayload={(v) => v}
        buildUpdatePayload={(lid, name) => ({ label_name: name })}
        getItemLabel={(i) => i.label_name}
        coloredBadges
        extraFormFields={<WorkColorPickerField name="color" label="رنگ" />}
      />
      <Modal
        open={colModal}
        title={label('وضعیت')}
        onCancel={() => setColModal(false)}
        onOk={saveColumn}
        confirmLoading={colModalSaving}
        okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
        destroyOnClose
      >
        <Form form={colForm} layout="vertical" className="work-panel-form">
          <Form.Item name="column_name" label={label('نام')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <WorkColorPickerField name="label_color" label="رنگ" />
          <Form.Item name="priority" label={label('اولویت نمایش')}>
            <Input type="number" />
          </Form.Item>
        </Form>
      </Modal>
    </WorkFormPage>
  );
};

export default AddTask;
